// Comparison table column picker (Views/Partials/blockgrid/Components/table.cshtml).
// A comparison table shows every column only while they all fit; otherwise it shows its
// row headers and one column, chosen from a select. Whether it fits depends on how many
// columns it has and what is in them, not the screen alone, so it is measured: the section
// gets data-fit="full" or "stacked", re-checked as its width changes.
// Cells carry data-col; the unchosen columns get is-inactive, which only hides them while
// stacked (see _table-block.scss).

// A column narrower than this squeezes its wrapped labels into a word a line.
const MIN_COLUMN = 96;
// Breathing room beside the widest unbreakable content (a price).
const COLUMN_SLACK = 8;

export function initTableColumnPickers() {
    document.querySelectorAll<HTMLSelectElement>('select[data-table-column-picker]').forEach(select => {
        const table = document.getElementById(select.dataset.tableColumnPicker ?? '') as HTMLTableElement | null;
        const section = table?.closest<HTMLElement>('.table-block');
        if (!table || !section) {
            return;
        }

        const show = (column: string) => {
            table.querySelectorAll<HTMLElement>('[data-col]').forEach(cell => {
                const col = cell.dataset.col;
                cell.classList.toggle('is-inactive', col !== '0' && col !== column);
            });
        };

        // Lay the table out in full and check its (equal, fixed-layout) columns are wide
        // enough for the widest unbreakable content - the large text - plus cell padding.
        const fits = () => {
            section.dataset.fit = 'full';
            const column = table.querySelector<HTMLElement>('thead th[data-col="1"]');
            if (!column) {
                return true;
            }
            const style = getComputedStyle(column);
            const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
            let widest = 0;
            table.querySelectorAll<HTMLElement>('.table-block__large').forEach(large => {
                const range = document.createRange();
                range.selectNodeContents(large);
                widest = Math.max(widest, range.getBoundingClientRect().width);
            });
            const needed = Math.max(MIN_COLUMN, widest + padding + COLUMN_SLACK);
            return column.getBoundingClientRect().width >= needed;
        };

        // Watch the section, not the table: stacked, the table is capped narrower, so its own
        // width would not change as the window widens.
        let lastWidth = -1;
        const update = () => {
            const width = section.clientWidth;
            if (width === lastWidth) {
                return;
            }
            lastWidth = width;
            section.dataset.fit = fits() ? 'full' : 'stacked';
        };

        select.addEventListener('change', () => show(select.value));
        show(select.value);
        update();
        // Width changes only - stacking changes the height, which must not re-trigger.
        new ResizeObserver(update).observe(section);
        // The serif prices widen once the web font arrives, so measure again then.
        document.fonts?.ready.then(() => {
            lastWidth = -1;
            update();
        });
    });
}
